/**
 * The undo offer store (2.L deliverable 10), pure: one offer at a time, the fixed label copy and
 * the refusal texts. No clock here: the toast (UI r4) owns the countdown. Labels never carry
 * document text.
 */

/** How long an offer shows (the kit's countdown runs for exactly this). */
export const UNDO_MS = 10_000;

export interface UndoOffer {
  readonly batchId: string;
  readonly labelKey: string;
}

export interface OfferState {
  readonly current: UndoOffer | null;
}

export const NO_OFFER: OfferState = { current: null };

/** A newer offer replaces the older one. */
export function offer(_state: OfferState, next: UndoOffer): OfferState {
  return { current: { batchId: next.batchId, labelKey: next.labelKey } };
}

/** Closes the offer of that batch; another batch's offer (a newer one) stays. */
export function close(state: OfferState, batchId: string): OfferState {
  return state.current?.batchId === batchId ? NO_OFFER : state;
}

/** Fixed copy by label key: a route id, or `play.create:outcome`. */
export const UNDO_LABELS: Readonly<Record<string, string>> = {
  'links.reorder': 'Story order changed',
  'links.remove': 'Link removed',
  'placements.create': 'Date changed',
  'placements.update': 'Date changed',
  'docs.patchMeta': 'Field changed',
  'sessions.items.create': 'Run sheet changed',
  'sessions.items.update': 'Run sheet changed',
  'sessions.items.move': 'Run sheet changed',
  'sessions.items.remove': 'Run sheet changed',
  'sessions.attendance.put': 'Attendance changed',
  'sessions.create': 'Session created',
  'sessions.update': 'Session changed',
  'play.create': 'Scene marked',
  'play.create:outcome': 'Outcome changed',
  'sessions.captures.update': 'Capture changed',
  'sessions.captures.remove': 'Capture removed',
  // After (2.F); a rejected reveal has no label because it cannot be undone.
  'sessions.captures.promote': 'Capture resolved',
  'knowledge.create:confirm': 'Reveal confirmed',
  'sessions.finish': 'Session finished',
  'sessions.sources.upload': 'Source added',
  'sessions.sources.remove': 'Source removed',
  // The bootstrap wizard's steps (2.J).
  'bootstrap.sessions.apply': 'Past sessions added',
  'bootstrap.status.apply': 'Statuses mapped',
  'bootstrap.ledger.apply': 'Marked in bulk',
};

/** The label of a key; an unknown key reads plainly, never as the key. */
export function labelFor(labelKey: string): string {
  return UNDO_LABELS[labelKey] ?? 'Change made';
}

export const UNDONE_TEXT = 'Undone.';
export const ROW_CHANGED_TEXT = 'Not undone: it changed since.';
export const NOT_UNDOABLE_TEXT = "Not undone: this can't be undone.";
export const NO_ANSWER_TEXT = "Not undone: the server didn't answer.";
export const FAILED_TEXT = 'Not undone.';

interface Shaped {
  readonly name?: unknown;
  readonly code?: unknown;
  readonly body?: { readonly details?: { readonly reason?: unknown } };
}

/** What a failed `undo.apply` says; reads the error's shape only, so the store stays pure. */
export function refusalText(error: unknown): string {
  const e = (typeof error === 'object' && error !== null ? error : {}) as Shaped;
  if (e.name === 'NetworkError') return NO_ANSWER_TEXT;
  if (e.code === 'undo_refused') {
    const reason = e.body?.details?.reason;
    if (reason === 'row_changed') return ROW_CHANGED_TEXT;
    if (reason === 'not_undoable') return NOT_UNDOABLE_TEXT;
  }
  return FAILED_TEXT;
}

/** A refusal means the server answered: the batch will not undo now, so the toast closes. */
export function isRefusal(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as Shaped).code === 'undo_refused';
}
